import { Cloud, Network, Wifi } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { RollingNumber } from "@/components/ui/rolling-number";
import type { DeviceClassRollup } from "@/api/schemas/sites";

export function DeviceClassCard({ rollup }: { rollup: DeviceClassRollup }) {
  const online = rollup.byStatus.online ?? 0;
  const offline =
    (rollup.byStatus.offline ?? 0) + (rollup.byStatus.alerting ?? 0);
  const dormant = rollup.byStatus.dormant ?? 0;
  const pct =
    rollup.total === 0 ? 0 : Math.round((online / rollup.total) * 100);
  const allOk = offline === 0 && online === rollup.total - dormant;
  const statusLabel =
    rollup.total === 0
      ? null
      : allOk
        ? "All Online"
        : offline > 0
          ? "Devices Offline"
          : "Some Dormant";
  const Icon =
    rollup.label === "Appliances"
      ? Cloud
      : rollup.label === "Wireless"
        ? Wifi
        : Network;
  const emptyHint =
    rollup.label === "Wireless"
      ? "No access points have been added to this network."
      : rollup.label === "Switches"
        ? "No switches have been added to this network."
        : "No appliances have been added to this network.";
  return (
    <Card>
      <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-aurora-soft text-violet">
              <Icon size={18} />
            </div>
            <div>
              <div className="text-base font-bold text-fg">{rollup.label}</div>
              <div className="font-mono text-[11px] text-fg-mute">
                {rollup.total} device{rollup.total === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          {statusLabel ? (
            <Badge
              variant={allOk ? "ok" : offline > 0 ? "warn" : "neutral"}
              size="md"
            >
              {statusLabel}
            </Badge>
          ) : null}
        </div>

        {rollup.total === 0 ? (
          <div className="mt-3 rounded-lg border border-dashed border-line px-3 py-2 text-xs text-fg-mute">
            {emptyHint}
          </div>
        ) : (
          <div className="mt-3 flex items-baseline gap-3 text-sm">
            <span className="text-2xl font-extrabold text-mint">
              <RollingNumber
                value={online}
                format={(v) => String(Math.round(v))}
              />
            </span>
            <span className="text-fg-mute">Online</span>
            {dormant > 0 ? (
              <>
                <span className="text-2xl font-extrabold text-fg-dim ml-3">
                  <RollingNumber
                    value={dormant}
                    format={(v) => String(Math.round(v))}
                  />
                </span>
                <span className="text-fg-mute">Dormant</span>
              </>
            ) : null}
          </div>
        )}

        {rollup.total > 0 ? (
          <>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-line/40">
              <div
                className="h-full rounded-full bg-mint"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-1 text-right font-mono text-[10.5px] text-fg-mute">
              {pct}%
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
